<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, watch } from 'vue';
  import LoadingAnimation1 from '../loadingIcons/LoadingAnimation1.vue';
  import GenericTable2 from '../tables/GenericTable2.vue';
  import Data from '@/api/data';
  import Device from '@/api/device';

  // Definitions
  const props = defineProps({
    dataID: {
      type: String,
      default: null
    },
    alertDetails: {
      type: Object,
      default: null
    }
  });

  const emit = defineEmits(['alert-details', 'close']);

  const loading = ref(false);
  const grabbingDevices = ref(true);
  const devices = ref([]);
  const selectedDeviceIndexes = ref([]);
  const grabbingSubcollections = ref(false);
  const subcollections = ref({});
  const subcollectionOptions = ref([]);
  const subcollectionFields = ref([]);
  const error = ref('');
  const name = ref(props.alertDetails?.name || '');
  const type = ref(props.alertDetails?.type || 'value');
  const selectedDevices = ref(props.alertDetails?.devices || []);
  const source = ref(props.alertDetails?.source || '');
  const field = ref(props.alertDetails?.condition?.field || '');
  const operator = ref(props.alertDetails?.condition?.operator || '');
  const constant = ref(props.alertDetails?.condition?.constant || 0);
  const alertOnce = ref(props.alertDetails?.interval === 0 || false);
  const days = ref(Math.floor(props.alertDetails?.interval/86400) || 0);
  const hours = ref(Math.floor((props.alertDetails?.interval%86400)/3600) || 0);
  const minutes = ref(Math.floor((props.alertDetails?.interval%3600)/60) || 0);
  const description = ref(props.alertDetails?.description || '');

  // Lifecycle hooks
  onMounted(async() => {
    if (props.alertDetails) {
      await getDevices();

      // Find indexes of selected devices to pre-select them in the table
      selectedDeviceIndexes.value = props.alertDetails.devices.map(deviceId => 
        devices.value.findIndex(device => device.id === deviceId)
      );
    }
    else if (props.dataID) {
      await getSubcollections(props.dataID);
    }
    else {
      await getDevices();
    }
  });

  // Methods
  const getDevices = async() => {
    const response = await Device.get('');
    if (response.status === 'success') {
      for (const device of response.details) {
        devices.value.push({
          id: device.id,
          name: device.friendlyName,
        });
      }
    }
    grabbingDevices.value = false;
  }

  // Get the subcollections and their fields connected to the dataID.
  const getSubcollections = async(dataID) => {
    grabbingSubcollections.value = true;
    const response = await Data.get(dataID);
    if (response.statusCode === 200) {
      // Ignore subcollections that are not relevant to the timeseries.
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages', 'Alert_Database']
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details).filter(([key]) => !ignoreSubcollections.includes(key))
      );

      // Get all keys from the details object and filter out the ignored ones
      subcollectionOptions.value = Object.keys(response.body.details)
        .filter(key => !ignoreSubcollections.includes(key));
    }
    grabbingSubcollections.value = false;
    getSubcollectionFields();
  }

  const getSubcollectionFields = () => {
    if (source.value) {
      const selectedSubcollection = subcollections.value[source.value];
      subcollectionFields.value = Object.entries(selectedSubcollection)
        .filter(([, value]) => value === 'int' || value === 'float')
        .map(([key]) => key);
    }
  }

  const devicesSelected = (selection) => {
    selectedDevices.value = selection.map(index => devices.value[index].id);
  }

  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    // convert alert days, hours, and minutes to seconds
    let totalSeconds = 0;
    if (!alertOnce.value) {
      totalSeconds = (days.value * 86400) + (hours.value * 3600) + (minutes.value * 60);
    }

    // Set up alert details
    const alertDetails = {
      alertID: props.alertDetails?.id || null,
      name: name.value,
      condition: { constant: constant.value, operator: operator.value, field: field.value },
      type: type.value,
      interval: totalSeconds,
      source: source.value,
      devices: selectedDevices.value,
      description: description.value,
      users: props.alertDetails?.users || [],
    }

    // Emit the new alert details and close the modal.
    emit('alert-details', alertDetails);
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  watch(selectedDevices, async() => {
    if (selectedDevices.value.length > 0) {
      await getSubcollections(selectedDevices.value[0]);
    }
  }, { immediate: true });

  watch(source, () => {
    getSubcollectionFields();
  });
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @click.self="close">
    <div class=" bg-color-f p-[20px] rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto">
      <h2 class=" font-extrabold text-xl underline text-center">Create A New Alert</h2>
      <br>
      <form @submit.prevent="handleSubmit">

        <!-- Alert name -->
        <label class="font-bold">
          Name:
          <br>
          <input v-model="name" type="text" class="w-full font-normal" required />
        </label>

        <!-- Alert type -->
        <label class="font-bold mt-5 block">
          Type:
          <br>
          <select v-model="type" class="w-full font-normal" required :disabled="true">
            <option value="geofence">Geofence</option>
            <option value="system">System Status</option>
            <option value="value">Value</option>
          </select>
        </label>

        <!-- Devices -->
        <h3 class="font-bold mt-5">Devices</h3>
        <div class="border border-gray-800 dark:border-gray-200 rounded-md">
          <GenericTable2
            :loading="grabbingDevices"
            :data="devices"
            :selectable="true"
            :pagination="false"
            :preSelect="selectedDeviceIndexes"
            @RowsSelected="devicesSelected"
          />
        </div>

        <!-- Condition -->
        <h3 class="font-bold mt-5" :class="{'opacity-50': selectedDevices.length === 0 || grabbingSubcollections}">Condition <LoadingAnimation1 v-if="grabbingSubcollections" class="inline-block ml-2"></LoadingAnimation1></h3>
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-5" :class="{'opacity-50': selectedDevices.length === 0 || grabbingSubcollections}">
          <label class="font-bold" :class="{'opacity-50': selectedDevices.length === 0 || grabbingSubcollections}">
            Source:
            <select v-model="source" class="w-full font-normal" :disabled="selectedDevices.length === 0">
              <option v-for="source in subcollectionOptions" :key="source" :value="source" required>{{ source }}</option>
            </select>
          </label>
          
          <div class="flex flex-wrap gap-2 mt-4 p-2 w-full">
            <div class="w-full sm:w-auto">
              Alert me when
              <select v-model="field" class="font-normal text-sm w-full sm:w-[150px] flex-shrink ml-1" :disabled="selectedDevices.length === 0" required>
                <option v-for="field in subcollectionFields" :key="field" :value="field" :disabled="selectedDevices.length === 0" required>{{ field }}</option>
              </select>
            </div>

            <div class="w-full sm:w-auto">
              is
              <select v-model="operator" class="font-normal text-sm w-full sm:w-[150px] flex-shrink ml-1" :disabled="selectedDevices.length === 0" required>
                <option value="==">equal to</option>
                <option value="!=">not equal to</option>
                <option value=">">greater than</option>
                <option value="<">less than</option>
              </select>
            </div>

            <div class="w-full sm:w-auto">
              the number
              <input v-model="constant" type="number" step="any" class="font-normal text-sm w-full sm:w-[80px] flex-shrink ml-1" :disabled="selectedDevices.length === 0" required />
            </div>
          </div>
        </div>

        <!-- Alert Every -->
        <h3 class="font-bold mt-5">Alert Every</h3>
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-5">
          <div class="flex flex-col md:flex-row gap-5">
            <label class="font-bold block w-full md:w-1/3" :class="{'opacity-50': alertOnce}">
              Days:
              <br>
              <input v-model="days" type="number" step="1" min="0" class="w-full font-normal" :disabled="alertOnce" :required="!alertOnce" />
            </label>

            <label class="font-bold block w-full md:w-1/3" :class="{'opacity-50': alertOnce}">
              Hours:
              <br>
              <input v-model="hours" type="number" step="1" min="0" class="w-full font-normal" :disabled="alertOnce" :required="!alertOnce" />
            </label>

            <label class="font-bold block w-full md:w-1/3" :class="{'opacity-50': alertOnce}">
              Minutes:
              <br>
              <input v-model="minutes" type="number" step="1" min="0" class="w-full font-normal" :disabled="alertOnce" :required="!alertOnce" />
            </label>
          </div>

          <label class="font-bold mt-2 block">
            <input type="checkbox" v-model="alertOnce" />
            Only alert once per event.
          </label>
        </div>

        <!-- Description -->
        <label class="font-bold mt-5 block">
          Alert Message:
          <br>
          <textarea v-model="description" class="w-full font-normal" rows="3" />
        </label>

        <!-- Error -->
        <div v-if="error" class="mt-5">
          <p class="font-normal ml-2 text-red-600 whitespace-pre-line">* {{ error }}</p>
        </div>

        <!-- Save and close -->
        <div class="flex justify-between mt-10">
          <button type="button" @click="close" class="default-button">Close</button>
          <LoadingAnimation1 v-if="loading"></LoadingAnimation1>
          <button v-else type="submit" :disabled="loading" class="default-button" :class="{'default-button-off': loading}">Create</button>
        </div>
      </form>
    </div>
    <div id="preview">

    </div>
  </div>
</template>
